import { FC, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { IMedication } from '../../caremap/ICareMap';
import { Ocr, TextDetections } from '@capacitor-community/image-to-text';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../../AlertPresenter';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { MedicationService } from './MedicationService';

export type IAddMedicationButtonProps = {
    onAdd: (initialValues?: IMedication, usedPhoto?: boolean) => void;
    onProcessingChange: (processing: boolean) => void;
    // called when the native photo picker is dismissed with no selection, so the caller can close
    // the choice sheet (which now stays open behind the picker until a file lands). Best-effort:
    // react-dropzone's onFileDialogCancel does not fire reliably on every platform, so the sheet
    // may simply remain open on cancel (the user can dismiss it or re-choose).
    onPickerCancel: () => void;
    // hands the dropzone's imperative `open()` back up to MedicationList, so it can register it
    // as AddMedicationLauncher's "Take a Photo" handler -- this component owns the dropzone/OCR
    // pipeline but never renders its own trigger button.
    onOpenReady: (open: () => void) => void;
};

/**
 * Headless photo-capture engine for the medication add flow. Renders no visible button --
 * MedicationList mounts this once (while canAddEdit) and wires the exposed `open()` into
 * AddMedicationLauncher, so the choice sheet's "Take a Photo" option can trigger this same
 * dropzone -> on-device OCR -> MedicationService.parseMedicationText pipeline it always has.
 */
export const AddMedicationButton: FC<IAddMedicationButtonProps> = ({ onAdd, onProcessingChange, onPickerCancel, onOpenReady }) => {
    const alert = useInstance(AlertPresenter);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const medicationService = useInstance(MedicationService);

    const { getInputProps, open } = useDropzone({
        noClick: true,
        onFileDialogCancel: onPickerCancel,
        onDropAccepted: async (files: File[]) => {
            onProcessingChange(true);

            const ocrResult: String[] = [];
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64 = reader.result as string;
                try {
                    const data: TextDetections = await Ocr.detectText({ base64: base64 });
                    for (const detection of data.textDetections) {
                        ocrResult.push(detection.text);
                    }

                    const parsedResponse = await medicationService.parseMedicationText({ content: ocrResult.join('') });

                    const generated = (attr: keyof IMedication) => parsedResponse[attr] ? true : false;
                    mixpanel.track('Medication Photo Processed', () => ({
                        'Name Generated': generated('name'),
                        'Instructions Generated': generated('instructions'),
                        'Dose Generated': generated('dose'),
                        'RX # Generated': generated('rxNumber'),
                        'Refills Generated': generated('refills'),
                        'Expiration Date Generated': generated('expiration'),
                    }));

                    onProcessingChange(false);
                    onAdd(parsedResponse, true);
                } catch (e) {
                    alert.showAndLogError(e);
                    onProcessingChange(false);
                }
            };

            try {
                reader.readAsDataURL(files[0]);
            } catch (e) {
                alert.showAndLogError(e);
                onProcessingChange(false);
            }
        }
    });

    useEffect(() => {
        onOpenReady(open);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <input {...getInputProps()} style={{display: 'none'}} />
    );
};
